import React from "react";
import { TableProps, Space, Tag, Button, Popconfirm, Tooltip } from "antd";
import { Pencil, Trash2, Layers, CheckCircle, ExternalLink } from "lucide-react";
import { IProduct } from "../../../types/api/product";

// Not config.siteUrl - see the same note in views/layout/Top.tsx.
const PUBLIC_SITE_URL = "https://artparks.co.uk";

interface GetProductColumnsProps {
  onEdit: (record: IProduct) => void;
  onEditVariants?: (record: IProduct) => void;
  onDelete: (id: string) => void;
  onApprove?: (record: IProduct) => void;
}

export const getProductColumns = ({
  onEdit,
  onEditVariants,
  onDelete,
  onApprove,
}: GetProductColumnsProps): TableProps<IProduct>["columns"] => [
  {
    title: "Image",
    dataIndex: "thumbnail",
    key: "thumbnail",
    render: (thumbnail?: string) =>
      thumbnail ? (
        <img
          src={thumbnail}
          alt="Sculpture"
          className="w-12 h-12 object-cover rounded border border-slate-200"
        />
      ) : (
        <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center text-slate-400 text-[9px]">
          No Image
        </div>
      ),
  },
  {
    title: "Title",
    dataIndex: "title",
    key: "title",
    render: (text: string) => (
      <span className="font-medium text-slate-800">{text}</span>
    ),
  },
  {
    title: "Artist",
    dataIndex: "artistName",
    key: "artistName",
    render: (artistName?: string) => artistName || "-",
  },
  {
    title: "Price",
    key: "price",
    render: (_, record: IProduct) =>
      record.isPriceOnApplication ? (
        <Tag color="orange">POA</Tag>
      ) : typeof record.price === "number" && record.price > 0 ? (
        `£${record.price.toLocaleString("en-GB")}`
      ) : (
        <span className="text-slate-400">-</span>
      ),
  },
  {
    title: "Slug",
    dataIndex: "slug",
    key: "slug",
    render: (text: string) => (
      <span className="text-slate-500 text-sm font-mono">{text}</span>
    ),
  },
  {
    title: "Status",
    key: "status",
    // pendingApproval takes priority over the plain Active/Inactive read -
    // status:false alone can't tell "artist just submitted/edited this,
    // needs a look" apart from "deliberately hidden" (Oliver's testing
    // doc, round 5).
    render: (_, record: IProduct) =>
      record.pendingApproval ? (
        <Tag color="gold">Awaiting Approval</Tag>
      ) : (
        <Tag color={record.status ? "green" : "red"}>
          {record.status ? "Active" : "Inactive"}
        </Tag>
      ),
  },
  {
    title: "Action",
    key: "action",
    width: 150,
    render: (_, record) => (
      <div className="flex items-center gap-1">
        {record.pendingApproval && onApprove && (
          <Tooltip title="Approve - publish this sculpture">
            <Popconfirm
              title="Approve this sculpture?"
              description="It will go live on the site."
              onConfirm={() => onApprove(record)}
              okText="Approve"
              cancelText="Cancel"
            >
              <Button
                size="small"
                icon={<CheckCircle size={14} />}
                className="text-emerald-600 hover:text-emerald-700 border-none shadow-none bg-transparent"
              />
            </Popconfirm>
          </Tooltip>
        )}
        <Tooltip title="Edit">
          <Button
            size="small"
            icon={<Pencil size={14} />}
            onClick={() => onEdit(record)}
            className="text-slate-500 hover:text-blue-600 border-none shadow-none bg-transparent"
          />
        </Tooltip>
        {record.slug && (
          <Tooltip title="View on site">
            <Button
              size="small"
              icon={<ExternalLink size={14} />}
              href={`${PUBLIC_SITE_URL}/sculpture/${record.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-emerald-600 border-none shadow-none bg-transparent"
            />
          </Tooltip>
        )}
        {onEditVariants && (
          <Tooltip title="Photos, Pricing & Editions">
            <Button
              size="small"
              icon={<Layers size={14} />}
              onClick={() => onEditVariants(record)}
              className="text-slate-500 hover:text-indigo-600 border-none shadow-none bg-transparent"
            />
          </Tooltip>
        )}
        <Tooltip title="Delete">
          <Popconfirm
            title="Delete this sculpture?"
            description="This action cannot be undone."
            onConfirm={() => onDelete(record._id)}
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
          >
            <Button
              size="small"
              icon={<Trash2 size={14} />}
              className="text-slate-500 hover:text-red-600 border-none shadow-none bg-transparent"
            />
          </Popconfirm>
        </Tooltip>
      </div>
    ),
  },
];
